import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Control, DeepPartial, useFieldArray, UseFormMethods } from 'react-hook-form';
import { DatePicker, Row, Col } from 'antd';
import moment from 'moment';

import { useDeepCompare } from 'hooks/use-deep-memo';
import { FormErrorMessageSpan, InputMintDiv } from 'views/common-styled-components';
import CustomRadio from 'components/custom-radio';
import { propsToJS } from '__utils/immutable-to-js';
import { useSelector } from 'react-redux';
import ProfileViewSelector from 'views/investor-profile/profile-view.selector';
import { relationshipMap } from 'constants/common-constants';

interface FormValues extends UseFormMethods {
  defaultOccupationValues: any;
  defaultValues?: {
    nomineeList?: DeepPartial<{
      nomineeName: string;
      relationship: string;
      nomineePercentage: any;
      isNomineeMinor: string;
      nomineeDOB: string;
      guardianName: string;
    }>[] | undefined;
    occupation?: boolean | undefined;
    incomeSlab?: any;
    aadhar?: any;
  },
  control: Control<{
    nomineeList: {
      nomineeName: string;
      relationship: string;
      nomineePercentage: any;
      isNomineeMinor: string;
      nomineeDOB: string;
      guardianName: string;
    }[];
    occupation: boolean | undefined;
    incomeSlab: any;
    aadhar: any;
  }>
}
const NomineeFieldArray: React.FC<FormValues> = ({ defaultValues, reset, control, register, getValues, errors, trigger, watch, defaultOccupationValues }: FormValues) => {
  const relationshipObj: any = [
  'Brother',
  'Daughter',
  'Father',
  'Mother',
  'Sister',
  'Son',
  'Spouse',
  'Aunt',
  'Brother-in-Law',
  'Daughter-in-Law',
  'Father-in-Law',
  'Grand Daughter',
  'Grand Father',
  'Grand Mother',
  'Grand Son',
  'Mother-in-Law',
  'Nephew',
  'Niece',
  'Sister-in-Law',
  'Son-in-Law',
  'Uncle',
  'Others'];

  const { fields, append, remove } = useFieldArray({
    control,
    name: "nomineeList"
  });
  const nomineeWatch = watch(`nomineeList`, fields);
  const { profileView } = propsToJS(useSelector(ProfileViewSelector));

  const nomineeNameHandler = (event: any) => {
    if (getValues().nomineeList.length > 1) {
      return getValues().nomineeList?.filter((ele: any) => event.toUpperCase() == ele.nomineeName.trim().toUpperCase()).length === 1;
    }
  }

  const nomineePercentageHandler = () => {
    let totalPercentage = 0;
    getValues().nomineeList?.forEach((ele: any, index: number) => {
      totalPercentage += +parseFloat(ele?.nomineePercentage).toFixed(2);
    });
    return totalPercentage == 100;
  }

  const addNominees = (event:any) => {
    event.preventDefault();
    const data = {
      nomineeName: '',
      relationship: '',
      nomineePercentage: '',
      isNomineeMinor: 'false'
    };
    fields?.length < 3 && append(data);
  }

  useEffect(() => {
    getValues().nomineeList?.forEach((ele: any, index: number) => {
      getValues(`nomineeList[${index}].nomineePercentage`) && trigger(`nomineeList[${index}].nomineePercentage`);
    });
  }, [fields])

  useEffect(() => {
    if(fields?.length==0) {
      // addNominees(event);
    }
  }, [fields?.length]);

  useEffect(() => {
    defaultOccupationValues?.nomineeList?.length && reset(defaultOccupationValues);
  }, [useDeepCompare(defaultOccupationValues)]);

  return (
    <>
      {fields.map((item, index) => (
        <div key={item.id} className="nominee-details">
          <h3>Nominee {index + 1}/3</h3>
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <InputMintDiv>
                <label htmlFor="nomineeName">Nominee Name</label>
                <br />
                <input
                  name={`nomineeList[${index}].nomineeName`}
                  id="nomineeName"
                  type="text"
                  placeholder="Enter nominee name"
                  defaultValue={item?.nomineeName ?? ''}
                  disabled={profileView?.registeredWithBse === 3}
                  ref={register({
                    required: "Please enter your nominee name",
                    pattern: {
                      value: /^[ a-zA-Z ]+$/,
                      message: "Please enter a valid name",
                    },
                    validate: {
                      duplicateError: nomineeNameHandler
                    }
                  })}
                  onInput={() => {
                    getValues().nomineeList?.forEach((ele: any, i: number) => {
                      trigger(`nomineeList[${i}].nomineeName`)
                    })
                  }}
                />
                {errors?.nomineeList?.[index]?.nomineeName && errors?.nomineeList?.[index]?.nomineeName.type !== 'duplicateError' ? <FormErrorMessageSpan>{errors.nomineeList?.[index]?.nomineeName.message}</FormErrorMessageSpan> : null}
                {
                  (errors?.nomineeList?.length > 1 && errors?.nomineeList?.[index]?.nomineeName && errors.nomineeList?.[index]?.nomineeName.type === 'duplicateError') &&
                  <FormErrorMessageSpan>Nominee name can't be same.</FormErrorMessageSpan>
                }
              </InputMintDiv>
            </Col>

            <Col xs={24} md={8}>
              <div className="form-group">
                <label htmlFor="relationship">Relationship with nominee </label>
                <br />
                <select
                  name={`nomineeList[${index}].relationship`}
                  id="relationship"
                  defaultValue={item?.relationship ?? ''}
                  disabled={profileView?.registeredWithBse === 3}
                  ref={register({
                    required:
                      "Please select your relationship with nominee",
                  })}
                >
                  <option disabled value="">Select relationship</option>
                  {Object.keys(relationshipMap).includes(item.relationship) ? (
                      Object.keys(relationshipMap).map((key) => (
                          <option key={key} value={key}>
                              {relationshipMap[key]}
                          </option>
                      ))
                  ) : (
                      relationshipObj.map((element: any) => (
                          <option key={element} value={element}>
                              {element}
                          </option>
                      ))
                  )}
                </select>

                {errors?.nomineeList?.[index]?.relationship ? <FormErrorMessageSpan>{errors.nomineeList?.[index]?.relationship.message}</FormErrorMessageSpan> : null}

              </div>
            </Col>
            <Col xs={24} md={8}>
              <InputMintDiv>
                <label htmlFor="nomineePercentage">Percentage</label>
                <br />
                <input
                  className="percentage"
                  name={`nomineeList[${index}].nomineePercentage`}
                  id="nomineePercentage"
                  type="number"
                  step="0.01"
                  placeholder="Enter percentage"
                  defaultValue={item.nomineePercentage ?? ''}
                  disabled={profileView?.registeredWithBse === 3}
                  ref={register({
                    required: "Please enter a valid nominee %",
                    pattern: {
                      value: /([1-9]|[1-9][0-9]|100)/,
                      message: "Please enter a valid %",
                    },
                    validate: {
                      percError: nomineePercentageHandler
                    }

                  })}
                  onInput={() => {
                    getValues().nomineeList?.forEach((ele: any, i: number) => {
                      trigger(`nomineeList[${i}].nomineePercentage`)
                    })
                  }}
                />
                {errors?.nomineeList?.[index]?.nomineePercentage && errors.nomineeList?.[index]?.nomineePercentage.type != "percError" ? <FormErrorMessageSpan>{errors.nomineeList?.[index]?.nomineePercentage.message}</FormErrorMessageSpan> : null}
                {
                  (errors?.nomineeList?.[index]?.nomineePercentage && errors.nomineeList?.[index]?.nomineePercentage.type === "percError") &&
                  <FormErrorMessageSpan>Total allocation should be 100%</FormErrorMessageSpan>
                }
              </InputMintDiv>
            </Col>

            {nomineeWatch[index]?.nomineeName &&
              <>
                <Col xs={24} md={8}>
                  <p style={{ margin: '7px 0px' }}>Is nominee minor?</p>
                  <div className="flex-row mb-20">
                    <CustomRadio
                      name={`nomineeList[${index}].isNomineeMinor`}
                      label="Yes"
                      value="true"
                      defaultChecked={item?.isNomineeMinor === true}
                      disabled={profileView?.registeredWithBse === 3}
                      radiobuttonRef={register()}
                    />
                    <CustomRadio
                      name={`nomineeList[${index}].isNomineeMinor`}
                      label="No"
                      value="false"
                      radiobuttonRef={register()}
                      defaultChecked={item?.isNomineeMinor === false || item?.isNomineeMinor === 'false'}
                      disabled={profileView?.registeredWithBse === 3}
                    />
                  </div>
                </Col>

                {nomineeWatch[index].isNomineeMinor === 'true' ? (
                  <>
                    <Col xs={24} md={8}>
                      <label>Nominee DOB</label><br />
                      <Controller
                        control={control}
                        name={`nomineeList[${index}].nomineeDOB`}
                        defaultValue={item?.nomineeDOB}
                        rules={{
                          required: "Please enter nominee DOB",
                        }}
                        render={ ({ onChange, value }) => (
                          <DatePicker
                            allowClear={false}
                            defaultValue={value && moment(value)}
                            value={value && moment(value)}
                            format='YYYY-MM-DD'
                            onChange={(e: any) => onChange(moment(e._d).format('YYYY-MM-DD'))}
                            placeholder='Enter nominee DOB'
                            disabledDate={date => date.isBefore(moment().subtract(18, 'year')) || date.isAfter(new Date())}
                            disabled={profileView?.registeredWithBse === 3}
                            style={{ height: "41px", marginTop: "4px", width: "100%" }}
                          />
                        )}
                      />
                      {errors?.nomineeList?.[index]?.nomineeDOB ? <FormErrorMessageSpan>{errors.nomineeList?.[index]?.nomineeDOB.message}</FormErrorMessageSpan> : null}
                    </Col>
                    <Col xs={24} md={8}>
                      <InputMintDiv>
                        <label htmlFor="guardianName">Guardian Name</label>
                        <br />
                        <input
                          name={`nomineeList[${index}].guardianName`}
                          type="text"
                          placeholder="Enter guardian name"
                          defaultValue={item?.guardianName ?? ''}
                          disabled={profileView?.registeredWithBse === 3}
                          ref={register({
                            required: "Please enter guardian name",
                            pattern: {
                              value: /^[ a-zA-Z ]+$/,
                              message: "Please enter a valid guardian name",
                            },
                          })}
                        />
                        {errors?.nomineeList?.[index]?.guardianName ? <FormErrorMessageSpan>{errors.nomineeList?.[index]?.guardianName.message}</FormErrorMessageSpan> : null}
                      </InputMintDiv>
                    </Col>
                  </>
                ) : null}

              </>}
          </Row>
          {nomineeWatch.length !== 1 &&
            <button className="remove-nominee" type="button" onClick={() => remove(index)}></button>
          }
          {nomineeWatch.length - 1 === index && nomineeWatch.length !== 3 && profileView?.registeredWithBse !== 3 && (
            <div className='flex-row' style={{ justifyContent: 'flex-end' }}>
              <button className="add-nominee" onClick={addNominees}>
                Add more Nominees
              </button>
            </div>
          )}
        </div>
      )
      )}
    </>
  );
}
export default NomineeFieldArray;